import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Download, ChevronUp, ChevronDown } from 'lucide-react';
import { 
  Table, 
  TableHeader, 
  TableBody, 
  TableRow, 
  TableHead, 
  TableCell 
} from '../../components/ui/table'; // Adjusted path
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card'; // Adjusted path
import { Button } from '../../components/ui/button'; // Adjusted path
import { formatDateString } from '../../lib/utils'; // Adjusted path

interface ParentProfile {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  updated_at: string;
}

type SortField = 'parentName' | 'email' | 'dateJoined';
type SortDirection = 'asc' | 'desc';

export default function ParentsWithoutPlayersReport() {
  const [parentsWithoutPlayers, setParentsWithoutPlayers] = useState<ParentProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reportGenerated, setReportGenerated] = useState(false);
  
  // Sorting state
  const [sortField, setSortField] = useState<SortField>('parentName');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  async function fetchParentsWithoutPlayers() {
    setLoading(true);
    setError('');
    setReportGenerated(true);
    setParentsWithoutPlayers([]); // Reset before fetching

    try {
      // 1. Fetch all profiles with the 'parent' role
      const { data: parentProfiles, error: profileError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email, updated_at')
        .eq('role', 'parent');

      if (profileError) {
        throw profileError;
      }

      if (!parentProfiles || parentProfiles.length === 0) {
        setParentsWithoutPlayers([]);
        return; // No parents found
      }

      // 2. Fetch all distinct parent_ids from player_parent_relationships
      const { data: relationships, error: relationshipError } = await supabase
        .from('player_parent_relationships')
        .select('parent_id', { count: 'exact', head: false }); // Optimization: only fetch parent_id

      if (relationshipError) {
        throw relationshipError;
      }

      const parentIdsWithPlayers = new Set(relationships?.map(rel => rel.parent_id) || []);

      // 3. Filter parent profiles
      const filteredParents = parentProfiles.filter(parent => 
        !parentIdsWithPlayers.has(parent.id)
      );

      setParentsWithoutPlayers(filteredParents);

    } catch (err: any) {
      console.error('Error fetching parents without players:', err);
      setError(err.message || 'Failed to fetch report');
    } finally {
      setLoading(false);
    }
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedParents = () => {
    return [...parentsWithoutPlayers].sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'parentName':
          const aName = `${a.last_name}, ${a.first_name}`;
          const bName = `${b.last_name}, ${b.first_name}`;
          comparison = aName.localeCompare(bName);
          break;
        case 'email':
          comparison = a.email.localeCompare(b.email);
          break;
        case 'dateJoined':
          comparison = new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime();
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  const exportCSV = () => {
    if (parentsWithoutPlayers.length === 0) return;
    
    const headers = ['Parent Name', 'Parent Email', 'Date Joined'];
    const sortedData = getSortedParents(); // Use sorted data for export

    const csvContent = [
      headers.join(','),
      ...sortedData.map(parent => [
        `"${parent.first_name} ${parent.last_name}"`,
        `"${parent.email}"`,
        `"${formatDateString(parent.updated_at, 'en-US', 'short')}"`
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `parents_without_players_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ChevronDown className="ml-1 h-4 w-4 text-gray-400" />;
    return sortDirection === 'asc' ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Parents Without Registered Players</CardTitle>
        <CardDescription>Parents who have accounts but have not yet registered any players.</CardDescription>
        <div className="flex justify-end space-x-2">
           <Button onClick={fetchParentsWithoutPlayers} disabled={loading}>
            {loading ? 'Generating...' : 'Generate Report'}
          </Button>
          {reportGenerated && parentsWithoutPlayers.length > 0 && (
            <Button variant="outline" onClick={exportCSV} disabled={parentsWithoutPlayers.length === 0}>
              <Download className="mr-2 h-4 w-4" /> Export CSV
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading && <p>Loading...</p>}
        {error && <p className="text-red-500">Error: {error}</p>}
        {reportGenerated && !loading && parentsWithoutPlayers.length === 0 && (
          <p>No parents found matching the criteria.</p>
        )}
        {reportGenerated && !loading && parentsWithoutPlayers.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="cursor-pointer" onClick={() => handleSort('parentName')}>
                  Parent Name <SortIcon field="parentName" />
                </TableHead>
                <TableHead className="cursor-pointer" onClick={() => handleSort('email')}>
                  Parent Email <SortIcon field="email" />
                </TableHead>
                <TableHead className="cursor-pointer" onClick={() => handleSort('dateJoined')}>
                  Date Joined <SortIcon field="dateJoined" />
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {getSortedParents().map((parent) => (
                <TableRow key={parent.id}>
                  <TableCell>{`${parent.first_name} ${parent.last_name}`}</TableCell>
                  <TableCell>{parent.email}</TableCell>
                  <TableCell>{formatDateString(parent.updated_at, 'en-US', 'short')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
